// PlansTab — shared subscription-plan UI for both Seller and Buyer portals.
// Pulls active plans for the given `audience` and lets the user self-subscribe
// via POST /api/{audience}/subscribe/{plan_id}. Backend records a (mocked)
// payment + bumps the user's plan + expiry. Re-fetches the user's status on
// every change so the "Current plan" badge stays in sync.

import React, { useEffect, useState } from "react";
import { Crown, Check, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

export default function PlansTab({ audience = "seller", subscription, flash }) {
  const [plans, setPlans] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [me, setMe] = useState(subscription || null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pl, sub] = await Promise.all([
        api.get(`/plans?audience=${audience}`),
        audience === "seller"
          ? api.get("/seller/subscription")
          : api.get("/buyer/subscription"),
      ]);
      setPlans(pl.data || []);
      setMe(sub.data);
    } catch (e) {
      flash?.(e?.response?.data?.detail || "Failed to load plans", true);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [audience]);

  const subscribe = async (plan) => {
    setBusyId(plan.plan_id);
    try {
      await api.post(`/${audience}/subscribe/${plan.plan_id}`);
      flash?.(`Subscribed to ${plan.name} 🎉`);
      loadAll();
    } catch (e) {
      flash?.(e?.response?.data?.detail || "Subscribe failed", true);
    } finally { setBusyId(null); }
  };

  const currentId = audience === "seller"
    ? (me?.plan?.plan_id || subscription?.subscription_plan_id)
    : me?.plan?.plan_id;
  const expiresAt = audience === "seller"
    ? me?.subscription_expires_at
    : me?.buyer_plan_expires_at;
  const active = audience === "seller"
    ? me?.subscription_active
    : me?.buyer_plan_active;

  if (loading) {
    return (
      <div className="card p-8 text-center text-[#A1A1AA] flex items-center justify-center gap-2">
        <Loader2 size={14} className="animate-spin"/> Loading plans…
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="card p-6 text-sm text-[#A1A1AA]" data-testid="plans-empty">
        No plans available right now. Check back later.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`plans-tab-${audience}`}>
      {/* Current plan banner */}
      {currentId && me?.plan && (
        <div className="card p-4 border-[#D4FF00]/30 bg-[#D4FF00]/5"
          data-testid="plans-current-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <ShieldCheck size={18} className="text-[#D4FF00]"/>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Current plan</div>
              <div className="font-display font-bold text-lg">{me.plan.name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Status</div>
              <div className={`font-bold text-sm ${active ? "text-[#D4FF00]" : "text-[#FF007F]"}`}>
                {active ? "Active" : "Inactive"}
              </div>
            </div>
            {expiresAt && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Renews / expires</div>
                <div className="font-display text-sm">
                  {new Date(expiresAt).toLocaleDateString(undefined, {
                    month: "short", day: "numeric", year: "numeric"
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const isCurrent = p.plan_id === currentId;
          const busy = busyId === p.plan_id;
          // Highlight middle/popular plan when 3 plans exist.
          const popular = plans.length >= 3 && plans.indexOf(p) === 1 && !isCurrent;
          return (
            <div key={p.plan_id}
              data-testid={`plan-card-${p.slug}`}
              className={`card p-5 flex flex-col gap-3 relative transition ${
                isCurrent
                  ? "border-[#D4FF00] shadow-[0_8px_32px_rgba(212,255,0,0.18)]"
                  : popular
                  ? "border-[#00F0FF]"
                  : ""
              }`}>
              {popular && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded">
                  Most popular
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-2 right-3 bg-[#D4FF00] text-black text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded flex items-center gap-1">
                  <Check size={10}/> Current
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">{p.name}</div>
                <Crown size={16} className={
                  p.price === 0 ? "text-[#A1A1AA]" :
                  p.price < 20 ? "text-[#00F0FF]" : "text-[#D4FF00]"
                }/>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display font-extrabold text-3xl">${p.price}</span>
                <span className="text-xs text-[#A1A1AA]">/{p.billing_cycle === "yearly" ? "yr" : "mo"}</span>
              </div>
              {p.description && (
                <div className="text-xs text-[#A1A1AA] leading-relaxed">{p.description}</div>
              )}
              <ul className="text-xs space-y-1.5 flex-1 mt-1">
                {(p.features || []).map((f, i) => (
                  <li key={`${p.plan_id || p._id || "p"}-feat-${i}-${f}`} className="flex items-start gap-2">
                    <Check size={12} className="text-[#D4FF00] shrink-0 mt-0.5"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(p)}
                disabled={busy || isCurrent}
                data-testid={`plan-subscribe-${p.slug}`}
                className={`mt-2 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition ${
                  isCurrent
                    ? "bg-[#3D2845] text-[#A1A1AA] cursor-not-allowed"
                    : busy
                    ? "bg-[#D4FF00]/50 text-black cursor-wait"
                    : "bg-[#D4FF00] text-black hover:bg-[#FAFAFA]"
                }`}>
                {isCurrent ? "Current plan"
                  : busy ? "Processing…"
                  : currentId ? "Switch to this" : (p.price === 0 ? "Activate" : "Subscribe")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment history */}
      {me?.payments?.length > 0 && (
        <div className="card p-5" data-testid="plans-history">
          <div className="font-display font-bold uppercase text-sm mb-3">Payment history</div>
          <div className="space-y-2">
            {me.payments.slice(0, 10).map((pay) => (
              <div key={pay.payment_id}
                className="flex items-center gap-3 py-2 border-b border-[#3D2845] last:border-0 text-xs">
                <div className="flex-1">
                  <div className="font-bold">{pay.note || pay.method || "Subscription"}</div>
                  <div className="text-[#A1A1AA]">
                    {new Date(pay.created_at).toLocaleString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit"
                    })}
                  </div>
                </div>
                <div className="font-display font-bold text-[#D4FF00]">
                  ${(pay.amount || 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
